import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import Button from "../button/Button";
import Icon from "../icon/Icon";
import LoadingSpinner from "../loading_spinner/LoadingSpinner";

const styles = require("./DocumentUploader.module.scss");

export type DefaultDocument = {
  id: string;
  name: string;
  size?: number;
  standard?: string;
  uploaded_at?: string;
};

type DocumentUploaderProps = {
  accept?: string;
  disabled?: boolean;
  error?: string;
  formName?: string;
  hint?: string;
  id?: string;
  isDocumentSelected?: boolean;
  isLoading?: boolean;
  isMultiple?: boolean;
  loadingTitle?: string;
  name?: string;
  onDocumentSelect?: (document: DefaultDocument) => void;
  onFileUpload?: (file: File) => void;
  onPreview?: () => void;
  required?: boolean;
  subtitle?: string;
  title?: string;
  uploadedDocument?: DefaultDocument | null;
};

const MAX_FILE_SIZE = 10 * 1024 * 1024;

function useDragDrop(disabled: boolean, onDrop: (e: React.DragEvent) => void) {
  const [isDragging, setIsDragging] = useState(false);
  const counter = useRef(0);

  if (disabled) {
    return { isDragging: false, dragHandlers: {} };
  }

  return {
    isDragging,
    dragHandlers: {
      onDragOver: (e: React.DragEvent) => e.preventDefault(),
      onDragEnter: (e: React.DragEvent) => { e.preventDefault(); counter.current++; setIsDragging(true); },
      onDragLeave: (e: React.DragEvent) => { e.preventDefault(); counter.current--; if (counter.current === 0) setIsDragging(false); },
      onDrop: (e: React.DragEvent) => { counter.current = 0; setIsDragging(false); onDrop(e); },
    },
  };
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return "";
  const thresh = 1024;
  if (bytes < thresh) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let u = -1;
  let b = bytes;
  do { b /= thresh; u++; } while (b >= thresh && u < units.length - 1);
  return `${b.toFixed(1)} ${units[u]}`;
}

function isValidFileType(file: File, accept?: string): boolean {
  if (!accept) return true;
  const mimeType = file.type;
  return accept.split(",").some((pattern) => {
    const type = pattern.trim().toLowerCase();
    if (type.startsWith(".")) return file.name.toLowerCase().endsWith(type);
    if (type.endsWith("/*")) return mimeType.startsWith(type.split("/*")[0] + "/");
    if (type.includes("/")) return mimeType === type;
    return (file.name.split(".").pop()?.toLowerCase() || "") === type;
  });
}

export default function DocumentUploader({
  accept,
  disabled,
  error,
  formName,
  hint,
  id,
  isDocumentSelected,
  isLoading,
  isMultiple,
  loadingTitle,
  name,
  onDocumentSelect,
  onFileUpload,
  onPreview,
  required,
  subtitle,
  title,
  uploadedDocument,
}: DocumentUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalError, setInternalError] = useState(error || "");
  const [selectedFile, setSelectedFile] = useState<DefaultDocument | null>(null);

  useEffect(() => {
    setInternalError(error || "");
  }, [error]);

  const isFormMode = !!name;
  const currentDocument = (isFormMode ? selectedFile : null) || uploadedDocument;
  const displayError = internalError;
  const isFilled = !!currentDocument && !isLoading;

  const hasMessage = Boolean(displayError) || Boolean(hint);
  const messageId = hasMessage ? `${id || "doc-uploader"}-message` : undefined;

  const openFilePicker = () => inputRef.current?.click();

  const processFile = (file: File, resetInput?: () => void) => {
    setInternalError("");
    if (isValidFileType(file, accept) && file.size <= MAX_FILE_SIZE) {
      isFormMode
        ? setSelectedFile({ id: "", name: file.name, size: file.size })
        : onFileUpload?.(file);
    } else {
      resetInput?.();
      setInternalError(
        currentDocument
          ? "To replace, please select a valid format and size document"
          : "Please select a valid format and size document"
      );
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file, () => { e.target.value = ""; });
    if (!isFormMode) e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (isFormMode && inputRef.current) {
      const dt = new DataTransfer();
      dt.items.add(file);
      inputRef.current.files = dt.files;
    }
    processFile(file, () => { if (inputRef.current) inputRef.current.value = ""; });
  };

  const handleClick = () => {
    if (isFilled && isMultiple) {
      onDocumentSelect?.(currentDocument);
    } else {
      openFilePicker();
    }
  };

  const { isDragging, dragHandlers } = useDragDrop(!!disabled, handleDrop);
  const showDropOverlay = isDragging && !(isFilled && isMultiple);
  const buttonSize = isMultiple ? "S" : "M";

  return (
    <div className={styles.container} aria-busy={isLoading}>

      {isLoading && (
        <div className={styles.loadingWrapper} role="status">
          <LoadingSpinner size="small" />
          <div className={styles.fileInfo}>
            <span className={styles.title}>{loadingTitle || "Uploading your document..."}</span>
          </div>
        </div>
      )}

      {!isLoading && (
        <div
          className={classNames({
            [styles.dropzone]: !isFilled || showDropOverlay,
            [styles.documentWrapper]: isFilled && !showDropOverlay,
            [styles.selected]: isFilled && isMultiple && isDocumentSelected,
            [styles.disabled]: disabled,
            [styles.error]: !!displayError,
            [styles.dragging]: showDropOverlay,
          })}
          {...(isMultiple ? {} : dragHandlers)}
          onClick={disabled ? undefined : handleClick}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled || undefined}
          aria-describedby={messageId}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !disabled) {
              e.preventDefault();
              e.stopPropagation();
              handleClick();
            }
          }}
          aria-label={
            showDropOverlay ? "Drop file to upload"
            : isFilled ? `${isMultiple ? "Select" : "Uploaded:"} ${currentDocument.name}`
            : "Upload document"
          }
          aria-pressed={isMultiple && isFilled ? isDocumentSelected : undefined}
        >
          {isFilled && isMultiple && formName && (
            <input
              type="radio"
              name={formName}
              value={currentDocument.id}
              checked={isDocumentSelected}
              onChange={() => onDocumentSelect?.(currentDocument)}
              className={styles.hiddenRadioInput}
              aria-label={`Select ${currentDocument.name}`}
            />
          )}

          <div className={styles.content}>
            {showDropOverlay ? (
              <div className={styles.fileInfo}>
                <span className={styles.dropText}>Drop it here</span>
              </div>
            ) : !isFilled ? (
              <div className={styles.fileInfo}>
                <div className={styles.uploadText}>
                  <span className={styles.uploadLink}>{title || "Click to upload"}</span>
                  <span className={styles.uploadBody}>or drop it here</span>
                </div>
                {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
              </div>
            ) : (
              <>
                {isMultiple && (
                  <Icon
                    name={isDocumentSelected ? "radioButtonChecked" : "radioButtonUnchecked"}
                    size="md"
                    color="talent"
                    aria-hidden="true"
                  />
                )}
                <div className={classNames(styles.fileInfo, styles.hasFile)}>
                  <span className={styles.title}>{currentDocument.name}</span>
                  <span className={styles.subtitle}>
                    {isMultiple ? "Uploaded now" : formatFileSize(currentDocument.size)}
                  </span>
                </div>
                <div className={styles.actionButtons}>
                  {onPreview && (
                    <Button
                      type="terceary"
                      size={buttonSize}
                      iconLeft="eye"
                      aria-label="Preview document"
                      onClick={(e) => { e.stopPropagation(); onPreview(); }}
                    />
                  )}
                  <Button
                    type="terceary"
                    size={buttonSize}
                    iconLeft="repeat"
                    aria-label="Replace document"
                    onClick={(e) => { e.stopPropagation(); openFilePicker(); }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {hasMessage && (
        <div
          id={messageId}
          role={displayError ? "alert" : undefined}
          aria-live={displayError ? "assertive" : "polite"}
          className={styles.hint}
        >
          <Icon
            name={displayError ? "triangleAlert" : "info"}
            size="sm"
            color={displayError ? "error" : "disable"}
            aria-hidden="true"
          />
          <span className={classNames(styles.hintText, displayError && styles.hintTextError)}>
            {displayError || hint}
          </span>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        id={id}
        name={name}
        disabled={disabled}
        required={required}
        onChange={handleFileChange}
        className={styles.hiddenInput}
        accept={accept}
        aria-label="Choose file to upload"
      />
    </div>
  );
}
